import React, { useMemo, useCallback } from "react";
import HighchartsComponent from "../HighchartsComponent";
import Highcharts from "highcharts";
import moment from "moment";

const defaultLegendStyle = {
    fontSize: "10px",
    fontWeigth: "600",
    color: "#595959",
};
const defaultMarkerConfig = {
    enabled: true,
    symbol: "circle",
};

export function TrendChart({
    services,
    title,
    subtitle = undefined,
    categories,
    series,
    width = null,
    height = null,
    minValue = undefined,
    maxValue = undefined,
    marginBottom = 120,
    pdfMode = false,
    referenceLine = undefined,
    enableLegend = true,
    legendStyle = defaultLegendStyle,
    markerConfig = defaultMarkerConfig,
    screenshotName = "trends",
    metadata,
    filters,
    id,
    section,
    options = {},
    scaleUnit = "default",
    rolledForwardTrendPoints = new Set(),
    ...props
}) {
    const getHighlightedMarkerSvg = useCallback((seriesColor, highlightColor) => {
        const width = 16,
            height = 16;
        const shapeSvg = `<circle cx="8" cy="8" r="6" fill="${seriesColor}" />`;
        const dotSvg = `<circle cx="8" cy="8" r="2.5" fill="${highlightColor}" />`;

        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
                ${shapeSvg}
                ${dotSvg}
            </svg>
        `.trim();
    }, []);

    const updatedSeries = useMemo(() => {
        if (!series) return [];
        return series.map((s) => ({
            ...s,
            data: s.data.map((point) => {
                if (!point) return null;
                const date = point[0];
                const value = point[1];
                const versionStd = s.name.trim();

                const dataPoint = { y: value };

                if (scaleUnit === "datetime") {
                    dataPoint.x = date;
                }

                const isRolledForward = metadata && rolledForwardTrendPoints.has(`${metadata?.custIndex}|${versionStd}|${date}`);
                if (isRolledForward) {
                    const seriesColor = s.color;
                    const svgIcon = getHighlightedMarkerSvg(seriesColor, "#BFBFBF");
                    dataPoint.marker = {
                        symbol: `url(data:image/svg+xml;base64,${window.btoa(svgIcon)})`,
                    };
                }

                return dataPoint;
            }),
        }));
    }, [series, rolledForwardTrendPoints, scaleUnit, metadata, getHighlightedMarkerSvg]);

    const scaleConfig = {
        default: {
            xAxis: {
                categories: categories,
            },
        },
        datetime: {
            xAxis: {
                type: "datetime",
                labels: {
                    rotation: -45,
                    formatter: function () {
                        return Highcharts.dateFormat("%e %b %Y", this.value);
                    },
                },
                tickmarkPlacement: "on",
            },
        },
    };

    return (
        <HighchartsComponent
            widgetProps={{
                id,
                filters,
                section,
                ...metadata,
            }}
            options={{
                caption: props.caption,
                chart: {
                    type: "spline",
                    width: width,
                    height: height,
                    zoomType: "xy",
                    marginBottom: marginBottom,
                    spacingBottom: 2,
                    panning: {
                        enabled: true,
                        type: "xy",
                    },
                    panKey: "shift",
                },
                plotOptions: {
                    series: {
                        marker: markerConfig,
                        dataLabels: {
                            enabled: true,
                        },
                        connectNulls: true,
                        // enableMouseTracking: mouseTracking,
                    },
                },
                tooltip: {
                    useHTML: true,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    shadow: true,
                    style: {
                        fontSize: "12px",
                        color: "#333",
                    },
                    formatter: function () {
                        let s = '<table style="border-collapse: collapse; width: 100%;">';
                        s += `<tr><th colspan="2" style="padding-bottom: 5px;"><b>${Highcharts.dateFormat("%e %b %Y", this.x)}</b></th></tr>`;
                        s += `<tr>
                                <td style="text-align: left; padding: 2px;"><b>${this.series.name}:</b></td>
                                <td style="text-align: right; padding: 2px;">${this.y}</td>
                            </tr>`;

                        s += "</table>";
                        return s;
                    },
                },
                legend: {
                    enabled: enableLegend,
                    // verticalAlign: 'middle',
                    // align: 'right',
                    padding: 12,
                    itemStyle: legendStyle,
                },
                title: {
                    text: title,
                    style: {
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#595959",
                    },
                },
                subtitle: {
                    text: subtitle,
                    align: "center",
                    style: {
                        fontWeight: 500,
                        fontStyle: "italic",
                    },
                },
                xAxis: {
                    title: {
                        text: null,
                    },
                    crosshair: true,
                    ...scaleConfig[scaleUnit].xAxis,
                },
                yAxis: {
                    min: minValue,
                    title: {
                        text: null,
                    },
                    minPadding: 0.1,
                    maxPadding: 0.18,
                    startOnTick: true,
                    endOnTick: true,
                    minTickInterval: 50,
                    events: {
                        // afterSetExtremes: function (e) {
                        //     if (minValue !== undefined && maxValue !== undefined) {
                        //         const padding = (e.dataMax - e.dataMin) * 0.1;
                        //         this.setExtremes(e.dataMin - padding, e.dataMax + padding);
                        //     }
                        // },
                    },
                    plotLines: referenceLine && [
                        {
                            value: referenceLine.value,
                            color: "#FF0000",
                            dashStyle: "shortdash",
                            width: 2,
                            zIndex: 10,
                        },
                    ],
                },
                responsive: {
                    rules: [
                        {
                            condition: {
                                maxWidth: 500,
                            },
                            chartOptions: {
                                legend: {
                                    enabled: false,
                                },
                                yAxis: {
                                    labels: {
                                        align: "left",
                                        x: 0,
                                        y: -5,
                                    },
                                },
                            },
                        },
                    ],
                },

                series: updatedSeries,
                lang: {
                    noData: "No data available",
                },
                noData: {
                    style: {
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#595959",
                    },
                },
                ...options,
            }}
            enableScreenshot={!pdfMode}
            imgName={screenshotName}
            {...props}
        />
    );
}
